import _ from "lodash";
import React, { useMemo } from "react";
import { connect } from "react-redux";
import { useI18n } from "Hooks";

import { Tooltip, Checkbox } from "@toddle-edu/ds-web";
import { StudentOutlined, FamilyOutlined } from "@toddle-edu/ds-icons";
import MessagingAvatar from "MessagingV2/components/ThreadList/Avatar/Avatar";
import {
  getDefaultNameSortPreference,
  getDefaultStudentNameOrderPreference,
} from "modules/CommonSelectors";
import { getUserInfo } from "Login/modules/LoginModule/LoginUtils";
import ConditionalWrapper from "Utils/ConditionalWrapper";
import { useMetadata } from "../MetadataProvider";

import {
  SUB_TEXT_LENGTH,
  getFormattedNameForUserType,
  getSubText,
} from "MessagingV2/modules/Utils";
import { isIdEqual } from "Utils";

import classNames from "classnames";
import classes from "./UserList.scss";

const customAvatarStyle = {
  size: "medium",
};

const UserTypeCard = ({
  avatarId,
  mainText,
  subText,
  profileImage,
  onAddUsers,
  onRemoveUsers,
  user,
  parents,
  isUserChecked,
  isParentChecked,
  isParentIndeterminate,
  isParentDisabled,
  isUserDisabled,
  isRevampEnabled,
  testId,
}) => {
  const { t } = useI18n(["common"]);
  const componentTestId = testId ? testId : "";
  const {
    disabledUserTooltipText,
    disabledNestedFamilyTooltipText,
  } = useMetadata();

  const onAddUser = () => onAddUsers([user]);
  const onAddParents = () => onAddUsers(parents);
  const onRemoveUser = () => onRemoveUsers([user]);
  const onRemoveParents = () => onRemoveUsers(parents);

  const onUserCheckboxChange = isUserChecked ? onRemoveUser : onAddUser;

  const onParentCheckboxChange =
    isParentChecked || isParentIndeterminate ? onRemoveParents : onAddParents;

  const isStudentType = _.isEqual(_.get(user, "type"), "student");

  const mainTextClasses = classNames({
    [classes.title]: !isRevampEnabled,
    [classes.titleRevamp]: isRevampEnabled,
    "text-body-1": !isRevampEnabled,
    "text-body": isRevampEnabled,
  });
  const subTitleClasses = classNames({
    [classes.userSubTitle]: !isRevampEnabled,
    [classes.userSubTitleRevamp]: isRevampEnabled,
    "text-body-2": !isRevampEnabled,
    "text-body-s": isRevampEnabled,
  });

  return (
    <div className={classes.userTypeCardContainer}>
      <MessagingAvatar
        id={avatarId}
        name={mainText}
        url={profileImage}
        customAvatarStyles={customAvatarStyle}
      />

      <div className={classes.userInfoContainer}>
        <div className={mainTextClasses}>{mainText}</div>
        {subText ? (
          <ConditionalWrapper
            wrapper={<Tooltip tooltip={subText} />}
            condition={_.size(subText) > SUB_TEXT_LENGTH}
            type={"wrapperAsElement"}
          >
            <div className={subTitleClasses}>
              {_.truncate(subText, { length: SUB_TEXT_LENGTH })}
            </div>
          </ConditionalWrapper>
        ) : null}
      </div>
      <div className={classes.checkboxContainer}>
        {!_.isEmpty(parents) ? (
          <div className={classes.checkbox}>
            <div className={classes.parentsIconAndCountContainer}>
              <div className={classNames("text-body-1", classes.parentsCount)}>
                {_.size(parents)}
              </div>
              <Tooltip
                placement="top"
                tooltip={
                  <div className="text-tooltip">
                    {t("common:add_family_members_of_student")}
                  </div>
                }
              >
                <FamilyOutlined variant="subtle" />
              </Tooltip>
            </div>
            <ConditionalWrapper
              condition={isParentDisabled}
              wrapper={
                <Tooltip
                  placement="top"
                  tooltip={t(disabledNestedFamilyTooltipText)}
                />
              }
              type={"wrapperAsElement"}
            >
              <Checkbox
                onChange={onParentCheckboxChange}
                disabled={isParentDisabled}
                isChecked={isParentChecked || isParentDisabled}
                isIndeterminate={isParentIndeterminate}
                testId={componentTestId}
              />
            </ConditionalWrapper>
          </div>
        ) : null}
        <div className={classes.checkbox}>
          {isStudentType ? (
            <Tooltip
              placement="top"
              tooltip={
                <div className="text-tooltip">{t("common:add_student")}</div>
              }
            >
              <StudentOutlined variant="subtle" />
            </Tooltip>
          ) : null}
          <ConditionalWrapper
            condition={isUserDisabled}
            wrapper={
              <Tooltip placement="top" tooltip={t(disabledUserTooltipText)} />
            }
            type={"wrapperAsElement"}
          >
            <Checkbox
              onChange={onUserCheckboxChange}
              isChecked={isUserChecked || isUserDisabled}
              disabled={isUserDisabled}
              testId={componentTestId}
            />
          </ConditionalWrapper>
        </div>
      </div>
    </div>
  );
};

const UserTypeList = ({
  defaultStudentNameSortPreference,
  defaultStudentNameOrderPreference,
  loggedInUserId,
  loggedInUserType,
  membersParams,
  selectedUsersState,
  onAddUsers,
  onRemoveUsers,
  headerTitle,
  prevUsers = [],
  showCohortHeader,
  isRevampEnabled,
  testId,
}) => {
  const { t } = useI18n(["common"]);

  const componentTestId = testId ? `${testId}-userTypeList` : "UserTypeList";
  const { userNodeList = [] } = membersParams;

  const prevUsersSet = useMemo(() => new Set(_.map(prevUsers, "id")), [
    prevUsers,
  ]);

  const headerContainer = classNames(classes.headerContainer, "text-label-2");

  if (_.isEmpty(userNodeList)) {
    return null;
  }

  return (
    <div className={classes.userListContainer}>
      {showCohortHeader ? (
        <div className={headerContainer}>{headerTitle}</div>
      ) : null}
      {_.map(userNodeList, userNode => {
        const user = _.get(userNode, "node", {});
        const isCurrentLoggedInUser = isIdEqual(loggedInUserId, user.id);

        const { id, profileImage } = user;

        let name = getFormattedNameForUserType({
          user,
          orderBy: defaultStudentNameSortPreference,
          defaultStudentNameOrderPreference,
          isFormattedNameV2: true,
        });

        if (isCurrentLoggedInUser) {
          name = t("common:you_with_full_name", { fullName: name });
        }

        const isUserChecked = !!_.get(selectedUsersState, [id], false);

        const familyMembers = _.get(user, "familyMembers", []);
        const isParentChecked = _.every(familyMembers, member =>
          _.get(selectedUsersState, [member.id], false)
        );

        const isParentIndeterminate =
          !isParentChecked &&
          _.some(familyMembers, member =>
            _.get(selectedUsersState, [member.id], false)
          );

        const isParentDisabled = _.every(familyMembers, member =>
          prevUsersSet.has(member.id)
        );

        const isUserDisabled = prevUsersSet.has(id);

        const subText = getSubText({
          loggedInUserRole: loggedInUserType,
          messagedUserObj: user,
          messagedUserRole: _.get(user, "type"),
          t,
          defaultStudentNameSortPreference,
        });

        return (
          <UserTypeCard
            key={id}
            onAddUsers={onAddUsers}
            onRemoveUsers={onRemoveUsers}
            avatarId={id}
            mainText={name}
            subText={subText}
            user={user}
            parents={familyMembers}
            profileImage={profileImage}
            isUserChecked={isUserChecked}
            isParentChecked={isParentChecked}
            isParentIndeterminate={isParentIndeterminate}
            isParentDisabled={isParentDisabled}
            isUserDisabled={isUserDisabled}
            isRevampEnabled={isRevampEnabled}
            testId={`${componentTestId}-userTypeCard-${id}`}
          />
        );
      })}
    </div>
  );
};

const mapStateToProps = state => {
  const defaultStudentNameSortPreference = getDefaultNameSortPreference(state);
  const defaultStudentNameOrderPreference = getDefaultStudentNameOrderPreference(
    state
  );
  const { user_type: loggedInUserType, id: loggedInUserId } = getUserInfo();

  return {
    defaultStudentNameSortPreference,
    defaultStudentNameOrderPreference,
    loggedInUserType,
    loggedInUserId,
  };
};

export default connect(mapStateToProps)(UserTypeList);
